import { useEffect, useState } from "react";
import { api, formatApiError, fmtNGN } from "@/lib/api";
import { openPaystackPopup } from "@/lib/paystack";
import { useAuth } from "@/context/AuthContext";
import { useMembershipUpgrade } from "@/components/TrialBanner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, TrendingUp, ArrowDownRight, ArrowUpRight, Loader2, Plus, Download, ShoppingBag, LogOut, AlertTriangle, Landmark, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";

const DEFAULT_WITHDRAW_TIERS = [1500, 5000, 10000, 35000, 75000, 150000, 250000, 500000, 750000, 1000000, 1200000];

function PwInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="h-12 rounded-xl mt-1 pr-11" data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-muted-foreground hover:text-foreground" data-testid={`${testid}-toggle`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

const labelFor = (t) => ({
  welcome_cashback: "Welcome Cash-back", daily_review: "Daily Review", referral: "Referral Reward",
  withdrawal: "Withdrawal", topup: "Top-up", purchase: "Artwork Purchase", promotional: "Promotional Reward",
  auction_sale: "Auction Sale", auction_capital: "Auction Capital", auction_profit: "Auction Profit",
  opt_out: "Opt-out Withdrawal",
  MEMBERSHIP_CASHBACK: "Welcome Cash-back", GROWTH_WALLET_FUNDING: "Growth Wallet Funding",
  WITHDRAWAL_REQUEST: "Withdrawal", WITHDRAWAL_SETTLEMENT: "Withdrawal Settlement",
  REVERSAL: "Payout Reversal", leader_bonus: "Leader Bonus",
  unapproved_withdrawal: "Unapproved Withdrawal — Refunded",
}[t] || t);

export default function Wallets() {
  const { refresh, user } = useAuth();
  const { upgrade, busy: upgrading } = useMembershipUpgrade();
  const isTrial = user?.role === "trial";
  const nav = useNavigate();
  const [reward, setReward] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [dialog, setDialog] = useState(null); // "topup" | "withdraw" | "setup"
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pw, setPw] = useState("");
  const [optQuote, setOptQuote] = useState(null);
  const [optPw, setOptPw] = useState("");
  const [setup, setSetup] = useState({ bank_name: "", account_number: "", account_name: "", withdrawal_password: "", bank_code: "" });
  const [banks, setBanks] = useState([]);
  const [bankQuery, setBankQuery] = useState("");
  const [confirmOwner, setConfirmOwner] = useState(false);
  const [sec, setSec] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [tiers, setTiers] = useState(DEFAULT_WITHDRAW_TIERS);

  const load = () => {
    api.get("/wallet/reward").then((r) => setReward(r.data));
    api.get("/wallet/growth").then((r) => setGrowth(r.data));
    api.get("/account/security-status").then((r) => setSec(r.data)).catch(() => {});
    api.get("/config").then((r) => { const t = r.data?.withdrawal_tiers; if (Array.isArray(t) && t.length) setTiers(t); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openSetup = async () => {
    setDialog("setup");
    if (banks.length === 0) {
      try { const { data } = await api.get("/banks"); setBanks(data.banks || []); } catch { /* ignore */ }
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("setup") === "1") {
      openSetup();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resolveAccount = async () => {
    if (!setup.bank_code || setup.account_number.length < 10) { toast.error("Select a bank and enter a valid 10-digit account number."); return; }
    setResolving(true);
    try {
      const { data } = await api.get(`/banks/resolve?account_number=${setup.account_number}&bank_code=${setup.bank_code}`);
      setSetup((s) => ({ ...s, account_name: data.account_name }));
      toast.success(`Account verified: ${data.account_name}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResolving(false); }
  };

  const openChangeAccount = async () => {
    setSetup({ bank_name: "", account_number: "", account_name: "", withdrawal_password: "", bank_code: "" });
    setConfirmOwner(false);
    setDialog("changeacct");
    if (banks.length === 0) {
      try { const { data } = await api.get("/banks"); setBanks(data.banks || []); } catch { /* ignore */ }
    }
  };

  const doChangeAccount = async () => {
    if (!setup.bank_code || !setup.account_number || !setup.account_name || !setup.withdrawal_password) {
      toast.error("Select your new bank, verify the account, and enter your fund password."); return;
    }
    if (!confirmOwner) { toast.error("Please confirm this is your personal/authorized account."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/account/change-receiving-account", { ...setup, confirm: true });
      toast.success(data.message || "Change submitted — pending admin approval.");
      setConfirmOwner(false); setDialog(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const openWithdraw = async () => {
    setAmount(""); setPw(""); setQuote(null);
    const { data } = await api.get("/wallet/withdraw/quote?amount=0");
    if (!data.bank_account || !data.has_password) { openSetup(); return; }
    setQuote(data);
    setDialog("withdraw");
  };

  const fetchQuote = async (val) => {
    if (!val || Number(val) <= 0) { setQuote((q) => q && { ...q, requested: 0 }); return; }
    try { const { data } = await api.get(`/wallet/withdraw/quote?amount=${Number(val)}`); setQuote(data); }
    catch { /* ignore */ }
  };

  const doTopup = async () => {
    if (Number(amount) < 5000) { toast.error("Minimum top-up is ₦5,000."); return; }
    setBusy(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/wallet/growth/fund/initialize", { amount: Number(amount), callback_url });
      openPaystackPopup(data, {
        onSuccess: () => nav(`/payment/callback?reference=${data.reference}`),
        onCancel: () => { setBusy(false); toast.message("Payment not completed."); },
      });
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const doWithdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/reward/withdraw", { amount: Number(amount), withdrawal_password: pw });
      await refresh(); load();
      toast.success("Your withdrawal is processing");      setDialog(null); setAmount(""); setPw("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const doSetup = async () => {
    if (!setup.bank_code || !setup.account_number || !setup.account_name || !setup.withdrawal_password) {
      toast.error("Select your bank, verify your account number, and set a withdrawal password."); return;
    }
    if (setup.withdrawal_password.length < 4) { toast.error("Withdrawal password must be at least 4 characters."); return; }
    if (!confirmOwner) { toast.error("Please confirm this is your personal/authorized account."); return; }
    setBusy(true);
    try { await api.post("/account/withdrawal-setup", { ...setup, confirm: true }); toast.success("Receiving account saved & verified"); setConfirmOwner(false); setDialog(null); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const openOptOut = async () => {
    setOptPw("");
    const { data } = await api.get("/wallet/opt-out/quote");
    if (!data.bank_account || !data.has_password) { openSetup(); return; }
    setOptQuote(data);
    setDialog("optout");
  };
  const doOptOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/opt-out", { withdrawal_password: optPw });
      await refresh(); load();
      toast.success(data.message || "Opt-out request submitted");
      setDialog(null); setOptPw("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!reward || !growth) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl md:text-4xl font-bold">Wallets</h1>
      <Tabs defaultValue="reward">
        <TabsList className="rounded-full">
          <TabsTrigger value="reward" data-testid="tab-reward" className="rounded-full">Reward Wallet</TabsTrigger>
          <TabsTrigger value="growth" data-testid="tab-growth" className="rounded-full">Growth Wallet</TabsTrigger>
        </TabsList>

        <TabsContent value="reward" className="space-y-5 mt-5">
          <div className="bg-primary text-primary-foreground rounded-3xl p-6 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 h-40 w-40 navy-gradient opacity-30 blur-2xl rounded-full" />
            <div className="flex items-center gap-2 text-primary-foreground/70 text-sm relative"><Wallet className="h-4 w-4" /> Reward Wallet Balance</div>
            <p className="font-heading text-5xl font-bold mt-2 relative" data-testid="reward-wallet-balance">{fmtNGN(reward.balance)}</p>
            <div className="flex gap-6 mt-3 text-sm relative">
              <span className="text-primary-foreground/70">Pending: {fmtNGN(reward.pending)}</span>
              <span className="text-primary-foreground/70">Withdrawable: {fmtNGN(reward.withdrawable)}</span>
            </div>
            {isTrial ? (
              <div className="mt-5 relative rounded-2xl bg-white/10 border border-gold/40 p-4" data-testid="trial-convert-nudge">
                <p className="text-sm font-semibold">You've earned {fmtNGN(reward.balance)} — activate to withdraw</p>
                <p className="text-[11px] text-primary-foreground/70 mt-1">Your trial rewards are locked. Activate membership (₦3,000) to unlock withdrawals and keep every naira you earn.</p>
                <Button variant="secondary" className="rounded-full mt-3" onClick={upgrade} disabled={upgrading} data-testid="trial-convert-btn">
                  {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate Membership"}
                </Button>
              </div>
            ) : (
              <>
                <Button variant="secondary" className="rounded-full mt-5 relative" onClick={openWithdraw} data-testid="withdraw-btn"><Download className="h-4 w-4 mr-2" /> Withdraw</Button>
                <p className="text-[11px] text-primary-foreground/60 mt-2 relative">Withdrawals process on Thursdays after the weekly auction. Fees: 5% platform + 7.5% tax. Min ₦1,500.</p>
              </>
            )}
          </div>
          <History txs={reward.transactions} />
        </TabsContent>

        <TabsContent value="growth" className="space-y-5 mt-5">
          <div className="sky-gradient rounded-3xl p-6 border border-sky-200">
            <div className="flex items-center gap-2 text-[#0b1d51]/70 text-sm"><TrendingUp className="h-4 w-4" /> Growth Wallet Balance</div>
            <p className="font-heading text-5xl font-bold mt-2 text-[#0b1d51]" data-testid="growth-wallet-balance">{fmtNGN(growth.balance)}</p>
            <p className="text-sm text-[#0b1d51]/70 mt-1">Used to purchase artworks. Auction sale proceeds land here.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {isTrial ? (
                <Button className="rounded-full navy-gradient text-white font-semibold" onClick={upgrade} disabled={upgrading} data-testid="trial-fund-activate-btn">
                  {upgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate to Add Funds"}
                </Button>
              ) : (
                <>
                  <Button className="rounded-full navy-gradient text-white font-semibold" onClick={() => { setDialog("topup"); setAmount(""); }} data-testid="topup-btn"><Plus className="h-4 w-4 mr-2" /> Add Funds</Button>
                  <Button variant="outline" className="rounded-full bg-white/60 border-[#0b1d51]/20 text-[#0b1d51] hover:bg-white" onClick={() => nav("/gallery")} data-testid="buy-artwork-btn"><ShoppingBag className="h-4 w-4 mr-2" /> Buy Artwork</Button>
                </>
              )}
            </div>
          </div>
          <History txs={growth.transactions} />
        </TabsContent>
      </Tabs>

      {/* Receiving Account */}
      {sec && (
        <div className="bg-card border border-border rounded-3xl p-5" data-testid="receiving-account-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${sec.bank_status === "VERIFIED" ? "bg-green-500/15" : "bg-gold/15"}`}>
                <Landmark className={`h-5 w-5 ${sec.bank_status === "VERIFIED" ? "text-green-600" : "text-gold"}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold">Receiving Account</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sec.bank_status === "VERIFIED" ? "bg-green-500/15 text-green-600" : "bg-gold/15 text-gold"}`} data-testid="panel-account-status">
                    {sec.bank_status === "VERIFIED" ? "Verified ✓" : (sec.bank_status === "REQUIRES_UPDATE" ? "Update Pending" : "Setup Required")}
                  </span>
                </div>
                {sec.bank_account ? (
                  <p className="text-xs text-muted-foreground truncate">{sec.bank_account.bank_name} • {sec.bank_account.account_number_masked} • {sec.bank_account.account_name}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Set up a bank account to receive payouts.</p>
                )}
                {sec.pending_bank_change && <p className="text-[11px] text-gold mt-0.5">A change request is pending admin approval.</p>}
              </div>
            </div>
            {sec.bank_account ? (
              <Button variant="outline" className="rounded-full" onClick={openChangeAccount} disabled={sec.pending_bank_change} data-testid="change-account-btn">Change Account</Button>
            ) : (
              <Button className="rounded-full navy-gradient text-white font-semibold" onClick={openSetup} data-testid="setup-account-btn">Set Up Account</Button>
            )}
          </div>
        </div>
      )}

      {/* Total Withdrawal & Opt-Out */}
      {!isTrial && (
      <div className="bg-destructive/5 border border-destructive/20 rounded-3xl p-5" data-testid="optout-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-destructive/10 grid place-items-center shrink-0"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
          <div className="flex-1">
            <h3 className="font-heading font-bold">Total Withdrawal & Opt-Out</h3>
            <p className="text-sm text-muted-foreground mt-1">Withdraw <strong>all</strong> your funds (Reward + Growth Wallets) and permanently exit AR Club. Platform fee (5%) and tax (7.5%) are deducted before your administrator transfers the balance. This closes your account — you'll need to re-register to rejoin.</p>
            <Button variant="outline" className="rounded-full mt-4 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={openOptOut} data-testid="optout-btn"><LogOut className="h-4 w-4 mr-2" /> Withdraw All & Opt Out</Button>
          </div>
        </div>
      </div>
      )}

      {/* Top-up dialog */}
      <Dialog open={dialog === "topup"} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading">Add Funds to Growth Wallet</DialogTitle><DialogDescription>Add money to buy artworks and join auctions.</DialogDescription></DialogHeader>
          <MoneyInput value={amount} onValueChange={setAmount} placeholder="Amount (₦) — min ₦5,000" className="h-12 rounded-xl" data-testid="amount-input" />
          <p className="text-xs text-muted-foreground">Minimum top-up is ₦5,000. A secure Paystack payment window will open to complete your payment.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="rounded-full navy-gradient text-white font-semibold" disabled={busy || Number(amount) < 5000} onClick={doTopup} data-testid="confirm-amount-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw dialog */}
      <Dialog open={dialog === "withdraw"} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading">Withdraw from Reward Wallet</DialogTitle><DialogDescription>Review the fee breakdown and confirm with your withdrawal password.</DialogDescription></DialogHeader>
          {!quote?.is_thursday && <div className="text-xs bg-destructive/10 text-destructive rounded-xl p-3">Withdrawals are only permitted on Thursdays after the weekly auction.</div>}
          <div className="space-y-3">
            <div>
              <Label>Select amount (₦)</Label>
              <Select value={amount ? String(amount) : undefined} onValueChange={(v) => { setAmount(v); fetchQuote(v); }}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="withdraw-amount"><SelectValue placeholder="Choose a withdrawal amount" /></SelectTrigger>
                <SelectContent>
                  {tiers.map((t) => (
                    <SelectItem key={t} value={String(t)} data-testid={`withdraw-tier-${t}`}>{fmtNGN(t)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {quote && Number(amount) > 0 && (
              <div className="bg-secondary rounded-2xl p-4 space-y-1 text-sm" data-testid="withdraw-breakdown">
                <Row l="Requested" v={fmtNGN(quote.requested)} />
                <Row l={`Platform Fee (${quote.platform_fee_pct}%)`} v={`- ${fmtNGN(quote.platform_fee)}`} />
                <Row l={`Tax (${quote.tax_pct}%)`} v={`- ${fmtNGN(quote.tax)}`} />
                <div className="border-t border-border my-1" />
                <Row l="Net Amount" v={fmtNGN(quote.net_amount)} bold />
                {quote.bank_account && <p className="text-xs text-muted-foreground pt-1">To: {quote.bank_account.bank_name} • {quote.bank_account.account_number_masked}</p>}
              </div>
            )}
            <div>
              <Label>Withdrawal Password</Label>
              <PwInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter your withdrawal password" testid="withdraw-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="rounded-full navy-gradient text-white font-semibold" disabled={busy || !amount || !pw} onClick={doWithdraw} data-testid="confirm-withdraw-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Withdrawal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opt-out dialog */}
      <Dialog open={dialog === "optout"} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Withdraw All & Opt Out</DialogTitle>
            <DialogDescription>This permanently closes your AR Club account. Review the breakdown and confirm with your withdrawal password.</DialogDescription>
          </DialogHeader>
          {optQuote?.opt_out_pending && <div className="text-xs bg-destructive/10 text-destructive rounded-xl p-3">You already have a pending opt-out request awaiting admin approval.</div>}
          <div className="text-sm bg-destructive/10 text-destructive rounded-xl p-3">
            Withdrawing all funds means a complete exit from AR Club. Your account will be closed after admin approval and you must re-register if you wish to rejoin.
          </div>
          {optQuote && (
            <div className="bg-secondary rounded-2xl p-4 space-y-1 text-sm" data-testid="optout-breakdown">
              <Row l="Reward Wallet" v={fmtNGN(optQuote.reward_wallet)} />
              <Row l="Growth Wallet" v={fmtNGN(optQuote.growth_wallet)} />
              <div className="border-t border-border my-1" />
              <Row l="Total" v={fmtNGN(optQuote.requested)} />
              <Row l={`Platform Fee (${optQuote.platform_fee_pct}%)`} v={`- ${fmtNGN(optQuote.platform_fee)}`} />
              <Row l={`Tax (${optQuote.tax_pct}%)`} v={`- ${fmtNGN(optQuote.tax)}`} />
              <div className="border-t border-border my-1" />
              <Row l="Net Payout" v={fmtNGN(optQuote.net_amount)} bold />
              {optQuote.bank_account && <p className="text-xs text-muted-foreground pt-1">To: {optQuote.bank_account.bank_name} • {optQuote.bank_account.account_number_masked}</p>}
            </div>
          )}
          <div>
            <Label>Withdrawal Password</Label>
            <PwInput value={optPw} onChange={(e) => setOptPw(e.target.value)} placeholder="Enter your withdrawal password" testid="optout-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="destructive" className="rounded-full" disabled={busy || !optPw || optQuote?.opt_out_pending || !(optQuote?.requested > 0)} onClick={doOptOut} data-testid="confirm-optout-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Opt-Out"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup dialog */}
      <Dialog open={dialog === "setup"} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading">Set up withdrawal account</DialogTitle><DialogDescription>Add your Nigerian bank account and a withdrawal password to enable payouts.</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">Withdrawals are processed on Thursdays after the weekly auction.</p>
          <div className="space-y-3">
            <div>
              <Label>Bank</Label>
              <Input value={bankQuery} onChange={(e) => setBankQuery(e.target.value)} placeholder="Search your bank…" className="h-10 rounded-xl mt-1" data-testid="bank-search" />
              <Select value={setup.bank_code || undefined} onValueChange={(v) => { const b = banks.find((x) => x.code === v); setSetup({ ...setup, bank_code: v, bank_name: b ? b.name : "", account_name: "" }); }}>
                <SelectTrigger className="h-11 rounded-xl mt-2" data-testid="setup-bank"><SelectValue placeholder="Select your bank" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {banks.filter((b) => b.name.toLowerCase().includes(bankQuery.toLowerCase())).slice(0, 40).map((b) => (
                    <SelectItem key={b.code} value={b.code} data-testid={`bank-${b.code}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Number</Label>
              <div className="flex gap-2 mt-1">
                <Input value={setup.account_number} onChange={(e) => setSetup({ ...setup, account_number: e.target.value.replace(/\D/g, "").slice(0, 10), account_name: "" })} className="h-11 rounded-xl" data-testid="setup-acct" placeholder="10-digit NUBAN" />
                <Button type="button" variant="outline" className="rounded-xl h-11 shrink-0" onClick={resolveAccount} disabled={resolving} data-testid="resolve-acct-btn">{resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
              </div>
            </div>
            <div><Label>Account Name</Label><Input value={setup.account_name} readOnly className="h-11 rounded-xl mt-1 bg-secondary" data-testid="setup-name" placeholder="Verified name appears here" /></div>
            <div><Label>Withdrawal Password</Label><PwInput value={setup.withdrawal_password} onChange={(e) => setSetup({ ...setup, withdrawal_password: e.target.value })} testid="setup-password" placeholder="Min 4 characters" /></div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={confirmOwner} onChange={(e) => setConfirmOwner(e.target.checked)} className="mt-0.5 accent-gold" data-testid="confirm-owner" />
              <span>I confirm that this is my personal/authorized receiving account and the information provided is correct.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="rounded-full navy-gradient text-white font-semibold" disabled={busy} onClick={doSetup} data-testid="save-setup-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change receiving account (requires fund password + admin approval) */}
      <Dialog open={dialog === "changeacct"} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading">Change receiving account</DialogTitle><DialogDescription>Enter your new bank account and current fund password. Changes require admin approval before going live.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Bank</Label>
              <Input value={bankQuery} onChange={(e) => setBankQuery(e.target.value)} placeholder="Search your bank…" className="h-10 rounded-xl mt-1" data-testid="change-bank-search" />
              <Select value={setup.bank_code || undefined} onValueChange={(v) => { const b = banks.find((x) => x.code === v); setSetup({ ...setup, bank_code: v, bank_name: b ? b.name : "", account_name: "" }); }}>
                <SelectTrigger className="h-11 rounded-xl mt-2" data-testid="change-bank"><SelectValue placeholder="Select your bank" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {banks.filter((b) => b.name.toLowerCase().includes(bankQuery.toLowerCase())).slice(0, 40).map((b) => (
                    <SelectItem key={b.code} value={b.code} data-testid={`chbank-${b.code}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Number</Label>
              <div className="flex gap-2 mt-1">
                <Input value={setup.account_number} onChange={(e) => setSetup({ ...setup, account_number: e.target.value.replace(/\D/g, "").slice(0, 10), account_name: "" })} className="h-11 rounded-xl" data-testid="change-acct" placeholder="10-digit NUBAN" />
                <Button type="button" variant="outline" className="rounded-xl h-11 shrink-0" onClick={resolveAccount} disabled={resolving} data-testid="change-resolve-btn">{resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
              </div>
            </div>
            <div><Label>Account Name</Label><Input value={setup.account_name} readOnly className="h-11 rounded-xl mt-1 bg-secondary" data-testid="change-name" placeholder="Verified name appears here" /></div>
            <div><Label>Current Fund Password</Label><PwInput value={setup.withdrawal_password} onChange={(e) => setSetup({ ...setup, withdrawal_password: e.target.value })} testid="change-fund-password" /></div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={confirmOwner} onChange={(e) => setConfirmOwner(e.target.checked)} className="mt-0.5 accent-gold" data-testid="change-confirm-owner" />
              <span>I confirm that this is my personal/authorized receiving account and the information provided is correct.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="rounded-full navy-gradient text-white font-semibold" disabled={busy} onClick={doChangeAccount} data-testid="submit-change-account-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Approval"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, bold }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={bold ? "font-bold" : ""}>{v}</span></div>;
}

function History({ txs }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} data-testid="toggle-history" className="w-full flex items-center justify-between bg-card border border-border rounded-2xl px-4 py-3 hover:bg-secondary/40 transition-colors">
        <h3 className="font-heading text-lg font-bold">Transaction History</h3>
        <span className="flex items-center gap-1 text-sm text-muted-foreground">{open ? "Hide" : `Show (${txs.length})`}{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>
      {open && (txs.length === 0 ? <p className="text-sm text-muted-foreground mt-3">No transactions yet.</p> : (
        <div className="space-y-2 mt-3" data-testid="tx-history">
          {txs.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-9 w-9 rounded-full grid place-items-center ${t.amount >= 0 ? "bg-gold/15" : "bg-secondary"}`}>
                  {t.amount >= 0 ? <ArrowDownRight className="h-4 w-4 text-gold" /> : <ArrowUpRight className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div>
                  <p className="text-sm font-medium">{labelFor(t.type)}</p>
                  <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}{t.status === "pending" ? " • Pending" : ""}</p>
                </div>
              </div>
              <span className={`font-bold ${t.amount >= 0 ? "text-gold" : ""}`}>{t.amount >= 0 ? "+" : ""}{fmtNGN(t.amount)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
